import { FC, useState } from 'react';

import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Image from 'quarks/Image';
import Text from 'quarks/Text';

import Form from 'components/Form';

import { colorTheme, toCamelCase } from 'utils/functions';
import richTextParser from 'utils/richTextParser';

import type { ContentfulAnnouncementBanner } from 'graphqlTypes';

type AnnouncementBannerProps = ContentfulAnnouncementBanner;

const AnnouncementBanner: FC<AnnouncementBannerProps> = ({
  heading,
  headingColor,
  body,
  bodyColor,
  formVersion,
  form,
  icon,
}) => {
  const [isClosed, setClosed] = useState(false);

  return (
    <Flex
      position={formVersion ? 'fixed' : 'relative'}
      bottom={formVersion ? '32px' : undefined}
      left={formVersion ? '50%' : undefined}
      transform={formVersion ? 'translateX(-50%)' : undefined}
      flexDirection="column"
      width="100%"
      marginX="auto"
      maxWidth={formVersion ? 'calc(100% - 32px)' : '100%'}
      backgroundColor={formVersion ? 'common-white' : 'green-300'}
      borderRadius={formVersion ? '12px' : '0'}
      boxShadow={formVersion ? 'lg' : 'unset'}
      alignItems="center"
      display={isClosed ? 'none' : 'flex'}
      overflowX="hidden"
      overflowY="hidden"
      paddingY={formVersion ? 0 : 12}
      zIndex={10}
      md={{ paddingY: formVersion ? 0 : 8, maxWidth: formVersion ? 'calc(100% - 48px)' : '100%' }}
      lg={{ maxWidth: formVersion ? 'calc(100% - 64px)' : '100%' }}
      xl={{ maxWidth: formVersion ? 'calc(1170px)' : '100%' }}
    >
      <Flex
        flexDirection="column"
        position="relative"
        width="100%"
        maxWidth="1320px"
        md={{ justifyContent: formVersion ? 'space-between' : 'center', flexDirection: 'row' }}
      >
        <Flex
          flexDirection="column"
          alignItems={formVersion ? 'center' : 'flex-start'}
          marginTop={formVersion ? 40 : 0}
          gap="4px"
          paddingX={16}
          paddingY={formVersion ? 16 : 0}
          sm={{ paddingX: 24, paddingY: formVersion ? 12 : 0 }}
          md={{ gap: '16px', flexDirection: 'row', alignItems: 'center', marginTop: 0 }}
          lg={{ paddingX: 32, paddingY: formVersion ? 16 : 0 }}
        >
          {icon && (
            <Container width="40px" height="40px" display="none" lg={{ display: 'block' }}>
              <Image image={icon} alt={icon?.title || ''} width="40px" height="40px" />
            </Container>
          )}
          {heading && (
            <Text
              textStyle="textMd"
              fontWeight="bold"
              textAlign={formVersion ? 'center' : 'left'}
              textColor={colorTheme(toCamelCase(headingColor || 'Gray 900'))}
              md={{ textAlign: 'left' }}
            >
              {heading}
            </Text>
          )}
          {body && (
            <Flex
              fontSize="textSm"
              lineHeight="textSm"
              textColor={colorTheme(toCamelCase(bodyColor || 'Gray 900'))}
              css={`
                a {
                  font-weight: 500;
                  text-decoration: underline;
                  color: ${color.gray[900]};
                }
              `}
            >
              {richTextParser(body)}
            </Flex>
          )}
        </Flex>
        {form && (
          <Flex
            alignItems="center"
            paddingBottom={16}
            paddingX={16}
            sm={{ paddingX: 24, paddingRight: 40 }}
            md={{ paddingBottom: 0 }}
            lg={{ paddingX: 32, paddingRight: 64 }}
          >
            <Form {...form} formType="Inline Subscribe" />
          </Flex>
        )}
        <Flex
          position="absolute"
          top={formVersion ? '25px' : '5px'}
          right="16px"
          cursor="pointer"
          onClick={() => setClosed(true)}
          sm={{ right: formVersion ? '12px' : '24px' }}
          md={{ top: formVersion ? '30px' : '5px' }}
          lg={{ top: formVersion ? '25px' : '5px', right: formVersion ? '16px' : '32px' }}
        >
          <Icon id="x-close" fill={color.gray[900]} size={20} />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default AnnouncementBanner;
